// The message registry: the single list that both the unit tests and the
// JSON-Schema/fixture generator iterate. Every wire message type appears here
// exactly once, each with its zod schema plus valid and invalid example
// payloads. Because tests and the Go-facing export are driven from the same
// table, coverage of "every message type" is structural, not aspirational.

import type { z } from 'zod';
import * as ws from './ws.js';
import * as rest from './rest.js';
import { BootstrapHint, EndpointList } from './endpoints.js';
import { ApiError, PushPayload } from './models.js';

export interface MessageSpec {
  /** Stable, sortable key; also the emitted filename stem. */
  readonly key: string;
  readonly schema: z.ZodType;
  /** At least one payload every implementation must accept. */
  readonly valid: readonly unknown[];
  /** At least one payload every implementation must reject. */
  readonly invalid: readonly unknown[];
}

// --- Reusable sample values -------------------------------------------------

const UUID_A = '11111111-1111-4111-8111-111111111111';
const UUID_B = '22222222-2222-4222-8222-222222222222';
const CALL_ID = '33333333-3333-4333-8333-333333333333';
const PUBKEY = 'A'.repeat(43) + '=';
const SIG = 'A'.repeat(86) + '==';
const BLOB = 'QUJD'; // base64("ABC")
const TS = '2026-07-08T12:00:00Z';
const TOKEN = 'header.body.signature';
const SDP = 'v=0\r\no=- 0 0 IN IP4 0.0.0.0\r\n';

// --- WS envelope helpers ----------------------------------------------------

const frame = (type: string, payload: unknown, seq = 0): unknown => ({
  type,
  seq,
  payload,
});

const FRIEND_REQUEST = {
  id: UUID_A,
  fromUserId: UUID_A,
  toUserId: UUID_B,
  status: 'pending',
  fromDisplayName: 'Ada',
  createdAt: TS,
};

const FRIEND = {
  userId: UUID_B,
  publicKey: PUBKEY,
  displayName: 'Grace',
  online: true,
  presence: 'app',
  lastSeenAt: TS,
  avatarSeed: 3,
};

const SESSION = {
  id: UUID_A,
  kind: 'web',
  label: 'Firefox on laptop',
  createdAt: TS,
  lastSeenAt: TS,
  current: true,
};

const PASSKEY_SUMMARY = {
  id: UUID_A,
  label: 'YubiKey 5C',
  createdAt: TS,
  lastUsedAt: TS,
};

// A stand-in for the opaque WebAuthn options / credential objects: any JSON
// object is accepted (the browser and go-webauthn own the real shape).
const WEBAUTHN_JSON = {
  id: 'A'.repeat(43),
  rawId: 'A'.repeat(43),
  type: 'public-key',
  response: { clientDataJSON: 'e30', attestationObject: 'o2M' },
};

const CALL_LOG_ENTRY = {
  id: CALL_ID,
  peerUserId: UUID_B,
  direction: 'outgoing',
  startedAt: TS,
  endedAt: TS,
  outcome: 'completed',
  seen: true,
};

const TOKEN_BUNDLE = {
  accessToken: TOKEN,
  refreshToken: TOKEN,
  expiresIn: 900,
  sessionId: UUID_A,
};

const ENDPOINT_LIST = {
  version: 17,
  signedBy: 'merkury-endpoint-key-v1',
  signature: SIG,
  endpoints: [
    { kind: 'domain', url: 'wss://app.merkury.example/v1/ws' },
    { kind: 'ip', url: 'wss://198.51.100.23/v1/ws', spkiPin: 'sha256/AAAA' },
  ],
  turn: [{ kind: 'domain', url: 'turns:turn1.example.com:443?transport=tcp' }],
  doh: ['https://cloudflare-dns.com/dns-query'],
};

// --- The registry -----------------------------------------------------------

export const MESSAGE_REGISTRY: readonly MessageSpec[] = [
  // ===== REST: registration & auth =====
  {
    key: 'rest.register.request',
    schema: rest.RegisterRequest,
    valid: [{ publicKey: PUBKEY, displayName: 'Ada' }],
    invalid: [
      { publicKey: PUBKEY }, // missing displayName
      { publicKey: 'too-short', displayName: 'Ada' }, // bad pubkey
      { publicKey: PUBKEY, displayName: 'Ada', extra: 1 }, // additional prop
    ],
  },
  {
    key: 'rest.register.response',
    schema: rest.RegisterResponse,
    valid: [{ userId: UUID_A }],
    invalid: [{ userId: 'not-a-uuid' }, {}],
  },
  {
    key: 'rest.auth.challenge.request',
    schema: rest.AuthChallengeRequest,
    valid: [{ userId: UUID_A }],
    invalid: [{ userId: 42 }, { userId: UUID_A, extra: true }],
  },
  {
    key: 'rest.auth.challenge.response',
    schema: rest.AuthChallengeResponse,
    valid: [{ nonce: BLOB, kdfSalt: BLOB }],
    invalid: [{ nonce: BLOB }, { nonce: '', kdfSalt: BLOB }, { nonce: '!!!!', kdfSalt: BLOB }],
  },
  {
    key: 'rest.auth.verify.request',
    schema: rest.AuthVerifyRequest,
    valid: [
      { userId: UUID_A, nonce: BLOB, signature: SIG },
      { userId: UUID_A, nonce: BLOB, signature: SIG, kind: 'android' }, // Capacitor session
      { userId: UUID_A, nonce: BLOB, signature: SIG, kind: 'ios' }, // native iOS session
      { userId: UUID_A, nonce: BLOB, signature: SIG, authKey: BLOB }, // 2FA password proof
      { userId: UUID_A, nonce: BLOB, signature: SIG, kind: 'web', authKey: BLOB },
    ],
    invalid: [
      { userId: UUID_A, nonce: BLOB, signature: 'short' },
      { userId: UUID_A, nonce: BLOB }, // missing signature
      { userId: UUID_A, nonce: BLOB, signature: SIG, kind: 'macos' }, // bad kind
      { userId: UUID_A, nonce: BLOB, signature: SIG, authKey: '' }, // empty authKey
    ],
  },
  {
    key: 'rest.auth.verify.response',
    schema: rest.AuthVerifyResponse,
    valid: [TOKEN_BUNDLE],
    invalid: [
      { ...TOKEN_BUNDLE, expiresIn: -1 },
      { accessToken: TOKEN, refreshToken: TOKEN, sessionId: UUID_A }, // missing expiresIn
    ],
  },
  {
    key: 'rest.auth.password.request',
    schema: rest.PasswordLoginRequest,
    valid: [
      { userId: UUID_A, authKey: BLOB },
      { userId: UUID_A, authKey: BLOB, kind: 'android' },
    ],
    invalid: [
      { userId: UUID_A }, // missing authKey
      { userId: UUID_A, authKey: 1 },
      { userId: UUID_A, authKey: BLOB, kind: 'macos' }, // bad kind
    ],
  },
  {
    key: 'rest.auth.password.response',
    schema: rest.PasswordLoginResponse,
    valid: [{ ...TOKEN_BUNDLE, encIdentityKey: BLOB }],
    invalid: [TOKEN_BUNDLE, { ...TOKEN_BUNDLE, encIdentityKey: 5 }],
  },
  {
    key: 'rest.auth.refresh.request',
    schema: rest.RefreshRequest,
    valid: [{ refreshToken: TOKEN }],
    invalid: [{}, { refreshToken: '' }],
  },
  {
    key: 'rest.auth.refresh.response',
    schema: rest.RefreshResponse,
    valid: [TOKEN_BUNDLE],
    invalid: [{ ...TOKEN_BUNDLE, sessionId: 'x' }],
  },

  // ===== REST: account =====
  {
    key: 'rest.me.password.request',
    schema: rest.SetPasswordRequest,
    valid: [
      { signature: SIG, authHash: BLOB, kdfSalt: BLOB, encIdentityKey: BLOB },
      { signature: SIG, authHash: null, kdfSalt: null, encIdentityKey: null }, // remove password
      {
        signature: SIG,
        authHash: BLOB,
        kdfSalt: BLOB,
        encIdentityKey: BLOB,
        terminateOthers: true,
      },
    ],
    invalid: [
      { authHash: null, kdfSalt: null, encIdentityKey: null }, // missing signature
      { signature: SIG, authHash: BLOB, kdfSalt: BLOB }, // missing encIdentityKey
    ],
  },
  {
    key: 'rest.me.get.response',
    schema: rest.MeResponse,
    valid: [
      {
        userId: UUID_A,
        publicKey: PUBKEY,
        displayName: 'Ada',
        hasPassword: false,
        createdAt: TS,
      },
      {
        userId: UUID_A,
        publicKey: PUBKEY,
        displayName: 'Ada',
        hasPassword: false,
        createdAt: TS,
        avatarSeed: 8,
      },
    ],
    invalid: [
      {
        userId: UUID_A,
        publicKey: PUBKEY,
        displayName: 'Ada',
        hasPassword: 'no',
        createdAt: TS,
      },
      {
        userId: UUID_A,
        publicKey: PUBKEY,
        displayName: 'Ada',
        hasPassword: false,
        createdAt: TS,
        avatarSeed: -1, // out of 0..8 range
      },
    ],
  },
  {
    // NB: the "at least one field" rule is a zod refinement that the JSON
    // Schema cannot express, so an empty {} is NOT listed as an invalid
    // fixture here (the schema-based Go contract test would wrongly accept
    // it); its rejection is covered by index.test.ts and by the Go handler.
    key: 'rest.me.patch.request',
    schema: rest.UpdateMeRequest,
    valid: [
      { displayName: 'Ada Lovelace' },
      { avatarSeed: 5 },
      { displayName: 'Ada', avatarSeed: 0 },
    ],
    invalid: [
      { displayName: 'x'.repeat(65) },
      { avatarSeed: 9 }, // out of 0..8 range
      { avatarSeed: 1.5 }, // not an integer
    ],
  },
  {
    key: 'rest.me.delete.request',
    schema: rest.DeleteMeRequest,
    valid: [{ signature: SIG }, { signature: SIG, authKey: BLOB }],
    invalid: [{}, { signature: SIG, authKey: 3 }],
  },
  {
    key: 'rest.me.aliases.get.response',
    schema: rest.AliasesBlobResponse,
    valid: [
      { blob: BLOB, updatedAt: TS },
      { blob: null, updatedAt: null }, // never uploaded
    ],
    invalid: [
      {}, // both fields required (nullable, not optional)
      { blob: BLOB }, // missing updatedAt
      { blob: '!!!!', updatedAt: TS }, // not base64
      { blob: BLOB, updatedAt: TS, extra: 1 },
    ],
  },
  {
    key: 'rest.me.aliases.put.request',
    schema: rest.PutAliasesBlobRequest,
    valid: [{ blob: BLOB }],
    invalid: [{}, { blob: null }, { blob: '' }, { blob: BLOB, extra: true }],
  },
  {
    key: 'rest.me.aliases.put.response',
    schema: rest.PutAliasesBlobResponse,
    valid: [{ updatedAt: TS }],
    invalid: [{}, { updatedAt: 12345 }],
  },

  // ===== REST: friends & requests =====
  {
    key: 'rest.friends.list.response',
    schema: rest.FriendsListResponse,
    valid: [
      { friends: [] },
      { friends: [FRIEND] },
      // avatarSeed is optional: a friend who never chose one omits it.
      { friends: [{ ...FRIEND, avatarSeed: undefined }] },
    ],
    invalid: [
      { friends: [{ ...FRIEND, online: 'yes' }] },
      { friends: [{ ...FRIEND, avatarSeed: 9 }] }, // out of 0..8 range
      {},
    ],
  },
  {
    key: 'rest.friends.requests.create.request',
    schema: rest.SendFriendRequestRequest,
    valid: [{ toUserId: UUID_B }],
    invalid: [{ toUserId: 'nope' }, {}],
  },
  {
    key: 'rest.friends.requests.list.response',
    schema: rest.FriendRequestsListResponse,
    valid: [{ incoming: [FRIEND_REQUEST], outgoing: [] }],
    invalid: [
      { incoming: [{ ...FRIEND_REQUEST, status: 'unknown' }], outgoing: [] },
      { incoming: [] }, // missing outgoing
    ],
  },

  // ===== REST: blocks =====
  {
    key: 'rest.blocks.list.response',
    schema: rest.BlocksListResponse,
    valid: [{ blocked: [] }, { blocked: [UUID_A, UUID_B] }],
    invalid: [{ blocked: ['bad'] }, {}],
  },
  {
    key: 'rest.blocks.create.request',
    schema: rest.BlockRequest,
    valid: [{ userId: UUID_B }],
    invalid: [{ userId: 1 }, {}],
  },

  // ===== REST: call history =====
  {
    key: 'rest.calls.list.response',
    schema: rest.CallsListResponse,
    valid: [{ calls: [] }, { calls: [CALL_LOG_ENTRY] }],
    invalid: [{ calls: [{ ...CALL_LOG_ENTRY, outcome: 'busy' }] }],
  },
  {
    key: 'rest.calls.seen.request',
    schema: rest.CallsSeenRequest,
    valid: [{}, { callIds: [CALL_ID] }],
    invalid: [{ callIds: ['nope'] }, { callIds: CALL_ID }],
  },

  // ===== REST: sessions =====
  {
    key: 'rest.sessions.list.response',
    schema: rest.SessionsListResponse,
    valid: [{ sessions: [SESSION] }, { sessions: [] }],
    invalid: [{ sessions: [{ ...SESSION, kind: 'macos' }] }],
  },
  {
    key: 'rest.sessions.patchCurrent.request',
    schema: rest.UpdateSessionRequest,
    valid: [
      {},
      { label: 'Pixel 8' },
      { push: { kind: 'ntfy', payload: 'topic' } },
      // APNs VoIP push token: hex device token opaque to the protocol.
      { push: { kind: 'apns', payload: '740f4707bebcf74f9b7c25d48e3358945f6aa01da5ddb387462c7eaf61bb78ad' } },
      { push: null },
    ],
    invalid: [
      { push: { kind: 'mpns', payload: 'x' } }, // bad push kind
      { label: 42 },
    ],
  },

  // ===== REST: passkeys (WebAuthn) =====
  {
    key: 'rest.passkeys.register.options.response',
    schema: rest.PasskeyRegisterOptionsResponse,
    valid: [{ publicKey: WEBAUTHN_JSON }, { publicKey: WEBAUTHN_JSON, prfSalt: BLOB }],
    invalid: [{}, [], 'not-an-object', 42, { prfSalt: BLOB }],
  },
  {
    key: 'rest.passkeys.register.request',
    schema: rest.PasskeyRegisterRequest,
    valid: [
      { credential: WEBAUTHN_JSON },
      { credential: WEBAUTHN_JSON, label: 'Work laptop' },
      { credential: WEBAUTHN_JSON, encIdentityKey: BLOB },
    ],
    invalid: [
      {}, // missing credential
      { credential: 'not-an-object' },
      { credential: WEBAUTHN_JSON, label: 'x'.repeat(65) }, // label too long
      { credential: WEBAUTHN_JSON, encIdentityKey: 5 }, // wrong type
      { credential: WEBAUTHN_JSON, extra: 1 }, // unknown wrapper key
    ],
  },
  {
    key: 'rest.passkeys.register.response',
    schema: rest.PasskeyRegisterResponse,
    valid: [{ passkey: PASSKEY_SUMMARY }, { passkey: { ...PASSKEY_SUMMARY, lastUsedAt: undefined } }],
    invalid: [{}, { passkey: { ...PASSKEY_SUMMARY, id: 'not-a-uuid' } }],
  },
  {
    key: 'rest.passkeys.list.response',
    schema: rest.PasskeysListResponse,
    valid: [{ passkeys: [] }, { passkeys: [PASSKEY_SUMMARY] }],
    invalid: [{}, { passkeys: [{ ...PASSKEY_SUMMARY, id: 'nope' }] }],
  },
  {
    key: 'rest.passkeys.login.options.request',
    schema: rest.PasskeyLoginOptionsRequest,
    // {} = discoverable (usernameless) ceremony; userId = legacy targeted flow.
    valid: [{ userId: UUID_A }, {}],
    invalid: [{ userId: 'nope' }, { userId: UUID_A, extra: 1 }],
  },
  {
    key: 'rest.passkeys.login.options.response',
    schema: rest.PasskeyLoginOptionsResponse,
    valid: [{ publicKey: WEBAUTHN_JSON }, { publicKey: WEBAUTHN_JSON, prfSalt: BLOB }],
    invalid: [{}, [], 'x', 7, { prfSalt: BLOB }],
  },
  {
    key: 'rest.passkeys.login.request',
    schema: rest.PasskeyLoginRequest,
    valid: [
      { userId: UUID_A, credential: WEBAUTHN_JSON },
      { userId: UUID_A, credential: WEBAUTHN_JSON, kind: 'android' },
      { credential: WEBAUTHN_JSON }, // discoverable: account read from userHandle
    ],
    invalid: [
      { userId: UUID_A }, // missing credential
      { userId: 'nope', credential: WEBAUTHN_JSON }, // malformed userId
      { userId: UUID_A, credential: WEBAUTHN_JSON, kind: 'macos' }, // bad kind
    ],
  },
  {
    key: 'rest.passkeys.login.response',
    schema: rest.PasskeyLoginResponse,
    valid: [TOKEN_BUNDLE, { ...TOKEN_BUNDLE, encIdentityKey: BLOB }],
    invalid: [{ ...TOKEN_BUNDLE, sessionId: 'x' }, { ...TOKEN_BUNDLE, encIdentityKey: 5 }],
  },

  // ===== REST: TURN, endpoints, health =====
  {
    key: 'rest.turn.credentials.response',
    schema: rest.TurnCredentialsResponse,
    valid: [
      {
        iceServers: [
          {
            urls: ['turns:turn1.example.com:443?transport=tcp'],
            username: '1767225600:abc',
            credential: 'c3VwZXI=',
          },
        ],
        ttlSeconds: 3600,
      },
    ],
    invalid: [
      { iceServers: [{ urls: [], username: 'u', credential: 'c' }], ttlSeconds: 3600 }, // empty urls
      { iceServers: [], ttlSeconds: 0 }, // ttl not positive
    ],
  },
  {
    key: 'rest.endpoints.response',
    schema: rest.EndpointsResponse,
    valid: [ENDPOINT_LIST],
    invalid: [{ ...ENDPOINT_LIST, endpoints: [] }],
  },
  {
    key: 'rest.healthz.response',
    schema: rest.HealthzResponse,
    valid: [{ status: 'ok', time: TS }],
    invalid: [{ status: 'up', time: TS }, { status: 'ok' }],
  },
  {
    key: 'rest.error.response',
    schema: ApiError,
    valid: [
      { code: 'rate_limited', message: 'slow down' },
      { code: 'not_friends', message: 'nope', ref: 'abc123' },
    ],
    invalid: [{ code: 'kaboom', message: 'x' }, { message: 'no code' }],
  },

  // ===== WS: sys.* =====
  {
    key: 'ws.sys.auth',
    schema: ws.SysAuthFrame,
    valid: [
      frame('sys.auth', { token: TOKEN }),
      frame('sys.auth', { token: TOKEN, resume: 'conn-1', lastSeq: 41 }),
      frame('sys.auth', { token: TOKEN, agent: 'reach' }), // native stay-reachable socket
    ],
    invalid: [
      frame('sys.auth', {}), // missing token
      frame('sys.auth', { token: TOKEN, agent: 'robot' }), // unknown agent
      frame('sys.auth', { token: TOKEN, lastSeq: -1 }), // negative seq
      { type: 'sys.auth', payload: { token: TOKEN } }, // missing envelope seq
    ],
  },
  {
    key: 'ws.sys.reauth',
    schema: ws.SysReauthFrame,
    valid: [frame('sys.reauth', { token: TOKEN })],
    invalid: [
      frame('sys.reauth', {}), // a renewal with nothing to renew to
      frame('sys.reauth', { token: '' }),
      // Renewal only: it may not smuggle the connection-opening fields, or it
      // becomes a second way to (re)authenticate an established socket.
      frame('sys.reauth', { token: TOKEN, resume: 'conn-1' }),
      frame('sys.reauth', { token: TOKEN, agent: 'reach' }),
    ],
  },
  {
    key: 'ws.sys.rpc',
    schema: ws.SysRpcFrame,
    valid: [
      frame('sys.rpc', { id: 'r1', method: 'GET', path: '/me' }),
      frame('sys.rpc', { id: 'r2', method: 'GET', path: '/friends/requests?dir=in' }),
      frame('sys.rpc', { id: 'r3', method: 'PATCH', path: '/me', body: { displayName: 'Ada' } }),
    ],
    invalid: [
      // An absolute URL would make the server a relay to anywhere; the socket
      // already decides which server this reaches.
      frame('sys.rpc', { id: 'r4', method: 'GET', path: 'https://elsewhere.example/me' }),
      frame('sys.rpc', { id: 'r5', method: 'GET', path: 'me' }), // not rooted
      frame('sys.rpc', { id: 'r6', method: 'TRACE', path: '/me' }), // method not in the set
      frame('sys.rpc', { id: '', method: 'GET', path: '/me' }), // no correlation id
      frame('sys.rpc', { id: 'r7', method: 'GET' }), // no path
    ],
  },
  {
    key: 'ws.sys.rpc.result',
    schema: ws.SysRpcResultFrame,
    valid: [
      frame('sys.rpc.result', { id: 'r1', status: 200, body: { userId: UUID_A } }, 3),
      frame('sys.rpc.result', { id: 'r2', status: 204 }, 4),
      frame('sys.rpc.result', { id: 'r3', status: 404, body: { code: 'not_found' } }, 5),
    ],
    invalid: [
      frame('sys.rpc.result', { id: 'r1', status: 99 }, 3), // not an HTTP status
      frame('sys.rpc.result', { id: 'r1', status: 600 }, 3),
      frame('sys.rpc.result', { status: 200 }, 3), // unmatchable to a request
    ],
  },
  {
    key: 'ws.sys.hello',
    schema: ws.SysHelloFrame,
    valid: [frame('sys.hello', { connId: 'conn-1', resumed: false, serverTime: 1767225600000 }, 1)],
    invalid: [
      frame('sys.hello', { connId: 'conn-1', resumed: false }), // missing serverTime
      frame('sys.hello', { connId: 'conn-1', resumed: 'no', serverTime: 1 }),
    ],
  },
  {
    key: 'ws.sys.ping',
    schema: ws.SysPingFrame,
    valid: [frame('sys.ping', {})],
    invalid: [frame('sys.ping', { extra: 1 }), frame('sys.pong', {})],
  },
  {
    key: 'ws.sys.pong',
    schema: ws.SysPongFrame,
    valid: [frame('sys.pong', {}, 2)],
    invalid: [frame('sys.pong', { x: 1 }), frame('sys.ping', {})],
  },
  {
    key: 'ws.sys.revoked',
    schema: ws.SysRevokedFrame,
    valid: [frame('sys.revoked', {}, 9)],
    invalid: [frame('sys.revoked', { reason: 'x' })],
  },

  // ===== WS: presence.* / friend.* =====
  {
    key: 'ws.presence.update',
    schema: ws.PresenceUpdateFrame,
    valid: [
      frame('presence.update', { userId: UUID_B, online: true }, 3),
      frame('presence.update', { userId: UUID_B, online: false, status: 'reachable' }, 4),
    ],
    invalid: [
      frame('presence.update', { userId: UUID_B }), // missing online
      frame('presence.update', { userId: UUID_B, online: true, status: 'away' }), // bad status
      frame('presence.update', { userId: 'x', online: true }),
    ],
  },
  {
    key: 'ws.friend.request',
    schema: ws.FriendRequestFrame,
    valid: [frame('friend.request', { request: FRIEND_REQUEST }, 4)],
    invalid: [
      frame('friend.request', { request: { ...FRIEND_REQUEST, status: 'nope' } }),
      frame('friend.request', {}),
    ],
  },
  {
    key: 'ws.friend.accepted',
    schema: ws.FriendAcceptedFrame,
    valid: [frame('friend.accepted', { request: { ...FRIEND_REQUEST, status: 'accepted' } }, 5)],
    invalid: [frame('friend.accepted', { request: { id: UUID_A } })],
  },

  // ===== WS: call.* =====
  {
    key: 'ws.call.invite',
    schema: ws.CallInviteFrame,
    valid: [
      frame('call.invite', {
        callId: CALL_ID,
        from: UUID_A,
        to: UUID_B,
        sdp: SDP,
        fingerprintSig: SIG,
        callerKeyFingerprint: 'sha256-abcdef',
      }),
      // Optional server-side ring deadline (additive; docs/02 §4 / TODO.md #4).
      frame('call.invite', {
        callId: CALL_ID,
        from: UUID_A,
        to: UUID_B,
        sdp: SDP,
        fingerprintSig: SIG,
        callerKeyFingerprint: 'sha256-abcdef',
        expiresAt: 1767225600000,
      }),
    ],
    invalid: [
      frame('call.invite', {
        callId: CALL_ID,
        from: UUID_A,
        to: UUID_B,
        sdp: SDP,
        fingerprintSig: 'short',
        callerKeyFingerprint: 'x',
      }),
      frame('call.invite', { callId: CALL_ID, from: UUID_A, to: UUID_B, sdp: SDP }), // missing sig fields
    ],
  },
  {
    key: 'ws.call.ringing',
    schema: ws.CallRingingFrame,
    valid: [frame('call.ringing', { callId: CALL_ID }, 6)],
    invalid: [frame('call.ringing', { callId: 'x' }), frame('call.ringing', {})],
  },
  {
    key: 'ws.call.accept',
    schema: ws.CallAcceptFrame,
    valid: [frame('call.accept', { callId: CALL_ID, sdp: SDP, fingerprintSig: SIG })],
    invalid: [
      frame('call.accept', { callId: CALL_ID, sdp: SDP }), // missing sig
      frame('call.accept', { callId: CALL_ID, sdp: '', fingerprintSig: SIG }), // empty sdp
    ],
  },
  {
    key: 'ws.call.decline',
    schema: ws.CallDeclineFrame,
    valid: [
      frame('call.decline', { callId: CALL_ID, reason: 'declined' }),
      frame('call.decline', { callId: CALL_ID, reason: 'busy' }),
    ],
    invalid: [
      frame('call.decline', { callId: CALL_ID, reason: 'timeout' }), // not a decline reason
      frame('call.decline', { callId: CALL_ID }),
    ],
  },
  {
    key: 'ws.call.ice',
    schema: ws.CallIceFrame,
    valid: [
      frame('call.ice', { callId: CALL_ID, candidates: [] }),
      frame('call.ice', {
        callId: CALL_ID,
        candidates: [
          { candidate: 'candidate:1 1 udp 2122260223 192.0.2.1 54400 typ host', sdpMid: '0', sdpMLineIndex: 0 },
          { candidate: '', sdpMid: null, sdpMLineIndex: null },
        ],
      }),
    ],
    invalid: [
      frame('call.ice', { callId: CALL_ID }), // missing candidates
      frame('call.ice', { callId: CALL_ID, candidates: [{ sdpMid: '0' }] }), // candidate missing
    ],
  },
  {
    key: 'ws.call.renegotiate',
    schema: ws.CallRenegotiateFrame,
    valid: [frame('call.renegotiate', { callId: CALL_ID, sdp: SDP, fingerprintSig: SIG })],
    invalid: [frame('call.renegotiate', { callId: CALL_ID, sdp: SDP })],
  },
  {
    key: 'ws.call.end',
    schema: ws.CallEndFrame,
    valid: [
      frame('call.end', { callId: CALL_ID, reason: 'hangup' }),
      frame('call.end', { callId: CALL_ID, reason: 'timeout' }),
      frame('call.end', { callId: CALL_ID, reason: 'error' }),
      frame('call.end', { callId: CALL_ID, reason: 'answered_elsewhere' }),
      frame('call.end', { callId: CALL_ID, reason: 'crossed' }),
    ],
    invalid: [
      frame('call.end', { callId: CALL_ID, reason: 'declined' }), // not an end reason
      frame('call.end', { callId: CALL_ID }),
    ],
  },

  {
    key: 'ws.call.sync',
    schema: ws.CallSyncFrame,
    valid: [
      // No tracked call.
      frame('call.sync', { call: null, serverTime: 1767225600000 }, 9),
      // Caller side, still ringing (with the ring deadline).
      frame('call.sync', {
        call: {
          callId: CALL_ID,
          role: 'caller',
          peerId: UUID_B,
          phase: 'ringing',
          expiresAt: 1767225600000,
        },
        serverTime: 1767225600000,
      }, 10),
      // Callee side, ringing, carrying the verbatim invite so a fresh session
      // can ring/upgrade without having received the original frame.
      frame('call.sync', {
        call: {
          callId: CALL_ID,
          role: 'callee',
          peerId: UUID_A,
          phase: 'ringing',
          expiresAt: 1767225600000,
          invite: {
            callId: CALL_ID,
            from: UUID_A,
            to: UUID_B,
            sdp: SDP,
            fingerprintSig: SIG,
            callerKeyFingerprint: 'sha256-abcdef',
          },
        },
        serverTime: 1767225600000,
      }, 11),
      // Answered (established) call — media-authoritative on the client.
      frame('call.sync', {
        call: { callId: CALL_ID, role: 'callee', peerId: UUID_A, phase: 'answered' },
        serverTime: 1767225600000,
      }, 12),
    ],
    invalid: [
      frame('call.sync', { call: null }), // missing serverTime
      frame('call.sync', { serverTime: 1 }), // missing call (must be present, even if null)
      frame('call.sync', {
        call: { callId: CALL_ID, role: 'caller', peerId: UUID_B, phase: 'timeout' }, // bad phase
        serverTime: 1,
      }),
      frame('call.sync', {
        call: { callId: CALL_ID, role: 'operator', peerId: UUID_B, phase: 'ringing' }, // bad role
        serverTime: 1,
      }),
      frame('call.sync', {
        call: { callId: 'not-a-uuid', role: 'caller', peerId: UUID_B, phase: 'ringing' },
        serverTime: 1,
      }),
    ],
  },

  // ===== WS: error =====
  {
    key: 'ws.error',
    schema: ws.ErrorFrame,
    valid: [
      frame('error', { code: 'callee_offline', message: 'peer not connected' }, 7),
      frame('error', { code: 'rate_limited', message: 'slow down', ref: 'r1' }, 8),
    ],
    invalid: [
      frame('error', { code: 'nope', message: 'x' }),
      frame('error', { message: 'no code' }),
    ],
  },

  // ===== Push payload (docs/06 §4 — the only data a push carries) =====
  {
    key: 'push.payload',
    schema: PushPayload,
    valid: [
      { type: 'incoming_call', callId: UUID_A },
      { type: 'missed_call', callId: UUID_A },
      { type: 'call_cancelled', callId: UUID_A },
      { type: 'incoming_call', callId: UUID_A, sentAt: 1_700_000_000_000 }, // freshness stamp
      { type: 'missed_call', callId: UUID_A, sentAt: 0 }, // epoch zero is a valid stamp
    ],
    invalid: [
      { type: 'incoming_call' }, // missing callId
      { type: 'text_message', callId: UUID_A }, // unknown type
      { type: 'incoming_call', callId: UUID_A, from: 'Ada' }, // extra key (no names in pushes)
      { type: 'incoming_call', callId: UUID_A, sentAt: -1 }, // stamp must be non-negative
      { type: 'incoming_call', callId: UUID_A, sentAt: 1.5 }, // stamp must be an integer
      { type: 'incoming_call', callId: UUID_A, sentAt: '1700000000000' }, // stamp must be a number
    ],
  },

  // ===== Endpoint-list document (docs/04 §2) =====
  {
    key: 'endpoints.document',
    schema: EndpointList,
    valid: [
      ENDPOINT_LIST,
      // Minimal document: turn/doh are optional, versions start at 1.
      {
        version: 1,
        signedBy: 'merkury-endpoint-key-v1',
        signature: SIG,
        endpoints: [{ kind: 'domain', url: 'wss://app.merkury.example/v1/ws' }],
      },
      // Android self-update metadata rides the same document (docs/06 §4).
      {
        ...ENDPOINT_LIST,
        apkVersion: 42,
        apkSha256: 'a'.repeat(64),
        apkMirrors: ['https://merkury.example/merkury.apk'],
      },
    ],
    invalid: [
      { ...ENDPOINT_LIST, signature: 'short' },
      { ...ENDPOINT_LIST, endpoints: [{ kind: 'domain', url: 'https://x/v1/ws' }] }, // not wss://
      { ...ENDPOINT_LIST, version: 0 }, // versions start at 1
      { ...ENDPOINT_LIST, version: -1 },
      { ...ENDPOINT_LIST, endpoints: [{ kind: 'ip', url: 'wss://198.51.100.23/v1/ws' }] }, // ip w/o pin
      { ...ENDPOINT_LIST, apkSha256: 'not-hex' }, // malformed APK digest
    ],
  },
  {
    key: 'endpoints.bootstrap.hint',
    schema: BootstrapHint,
    valid: [
      { hint: 1, endpoints: [{ url: 'wss://app.merkury.example/v1/ws' }] },
      {
        hint: 1,
        endpoints: [
          { url: 'wss://app.merkury.example/v1/ws' },
          { url: 'wss://cdn-spare-1.example/v1/ws' },
        ],
      },
    ],
    invalid: [
      // A hint must never be mistakable for a list: no version, no signature,
      // and nothing a client could adopt or persist.
      { hint: 1, endpoints: [{ url: 'wss://a.example/ws' }], version: 3 },
      { hint: 1, endpoints: [{ url: 'wss://a.example/ws' }], signature: SIG },
      { hint: 1, endpoints: [{ kind: 'domain', url: 'wss://a.example/ws' }] },
      { hint: 1, endpoints: [] }, // a hint with nothing to try is not a hint
      { endpoints: [{ url: 'wss://a.example/ws' }] }, // missing discriminator
      { hint: 2, endpoints: [{ url: 'wss://a.example/ws' }] },
    ],
  },
];

/** Look up a spec by key (used by tests). */
export const MESSAGE_KEYS: readonly string[] = MESSAGE_REGISTRY.map((m) => m.key);
